import * as mutations from "../../graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LoadingModal from "../../components/modals/LoadingModal";
import NotificationSnackbar from "../../components/NotificationSnackbar";
import EditCustomerModal from "../../components/modals/EditCustomerModal";
import DeletionModal from "../../components/modals/DeletionModal";
import DataTable from "../../components/DataTable";
import { useState, useEffect } from "react";
import { get } from "@aws-amplify/api";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { apiClient } from "../../helpers/apiClient";
import { useTranslation } from "react-i18next";
import { CGI_PURPLE, CGI_RED, packages } from "../../helpers/enums";
import {
  fetchAllAssessments,
  fetchAllAssessmentAssignmentsByCustomer,
  fetchAllCustomers,
} from "../../helpers/apiRequests";
import {
  Box,
  Container,
  Typography,
  Button,
  Switch,
  Tooltip,
  IconButton,
  List,
  ListItem,
} from "@mui/material";

export default function ManageCustomers() {
  const { t } = useTranslation();

  const { user } = useAuthenticator((context) => [context.user]);

  const emptyCustomer = { id: "", assessments: [], userIds: [] };
  const [openLoading, setOpenLoading] = useState(false);
  const [openEditCustomerModal, setOpenEditCustomerModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [toDeleteId, setToDeleteId] = useState("");
  const [toDeleteName, setToDeleteName] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(emptyCustomer);
  const [allAssessments, setAllAssessments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [customers, setCustomers] = useState([]);

  const adminApiName = "AdminQueries";
  const hiddenHeaders = ["id"];
  const exclude = [];
  const smallerFields = ["active", "trial"];
  const biggerFields = ["assessments", "userIds"];
  const specialFields = ["trial"];

  const headers =
    customers.length > 0
      ? [
          ...Object.keys(customers[0])
            .filter((key) => !exclude.includes(key))
            .map((key) => {
              return {
                field: key,
                headerName: t(key),
                flex: smallerFields.includes(key)
                  ? 1
                  : biggerFields.includes(key)
                  ? 3
                  : 2,
                hide: hiddenHeaders.includes(key),
                renderCell: (params) => {
                  const id = params.row.id;
                  const { value, field } = params;
                  if (typeof value === "boolean") {
                    if (specialFields.includes(field)) {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            height: "100%",
                          }}
                        >
                          {value && (
                            <Typography
                              sx={{
                                border: 2,
                                borderColor: CGI_PURPLE,
                                borderRadius: 5,
                                px: 0.7,
                              }}
                            >
                              TRIAL
                            </Typography>
                          )}
                        </Box>
                      );
                    } else {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            height: "100%",
                          }}
                        >
                          <Switch
                            size="small"
                            checked={value}
                            onChange={(event) => {
                              updateCustomerState(id, event.target.checked);
                            }}
                          />
                        </Box>
                      );
                    }
                  } else if (Array.isArray(value)) {
                    if (key == "userIds") {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            height: "100%",
                          }}
                        >
                          <List
                            sx={{
                              listStyleType: "circle",
                              p: 0,
                              ml: 2,
                            }}
                          >
                            {value.map((item, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  display: "list-item",
                                  p: 0,
                                }}
                              >
                                {item.email}
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      );
                    } else {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            height: "100%",
                          }}
                        >
                          <List
                            sx={{
                              listStyleType: "circle",
                              p: 0,
                              ml: 2,
                            }}
                          >
                            {value.map((item, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  display: "list-item",
                                  p: 0,
                                }}
                              >
                                {item.name} | {item.language} ({item.package})
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      );
                    }
                  }
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        height: "100%",
                      }}
                    >
                      {value}
                    </Box>
                  );
                },
              };
            }),
          {
            field: "manage",
            headerName: t("manageCustomer"),
            flex: 2,
            sortable: false,
            renderCell: ({ row }) => {
              const id = row.id;
              const customer = row;
              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    height: "100%",
                  }}
                >
                  <Tooltip title={t("editCustomer")}>
                    <IconButton onClick={(e) => setSelectedCustomer(customer)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>{" "}
                  <Tooltip title={t("deleteCustomer")}>
                    <IconButton
                      onClick={(e) => {
                        setToDeleteName(customer.name);
                        setToDeleteId(customer.id);
                        setOpenDelete(true);
                      }}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            },
          },
        ]
      : [];

  const rows = customers.map((row, index) => ({
    id: row.id ?? index,
    ...row,
  }));

  async function listUsers() {
    const path = "/listUsers";

    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();

    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };

    const restOperation = await get({
      apiName: adminApiName,
      path,
      options,
    });

    let parsed;
    try {
      const { body } = await restOperation.response;
      parsed = await body.json();
    } catch {
      parsed = restOperation?.data ?? restOperation;
    }

    parsed.Users.forEach((user, index) => {
      let toAdd = {};
      user.Attributes.forEach((att) => {
        toAdd[att.Name] = att?.Value;
      });
      parsed.Users[index]["Attributes"] = toAdd;
    });

    const filteredUsers = parsed.Users.filter((user) => user.Enabled !== false);
    const userArr = filteredUsers.map((e) => ({
      id: e.Username,
      email: e.Attributes.email,
    }));

    setAllUsers(userArr);
    return userArr;
  }

  async function listAllAssessments() {
    const fetchedAssessments = await fetchAllAssessments();
    const allAssessments = fetchedAssessments.map((assessment) => {
      return {
        id: assessment.id,
        name: assessment.name,
        language: assessment.language,
        ownerGroups: assessment.ownerGroups || [],
      };
    });
    setAllAssessments(allAssessments);
    return allAssessments;
  }

  async function updateCustomerState(customerId, newStatus) {
    try {
      await apiClient.graphql({
        query: mutations.updateCustomer,
        variables: {
          input: {
            id: customerId,
            active: newStatus,
          },
        },
      });
      setNotificationMessage(t("statusChanged!"));
    } catch {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
    fetchCustomers();
  }

  async function deleteCustomer(id) {
    try {
      const assessmentAssigments =
        await fetchAllAssessmentAssignmentsByCustomer(id);
      await Promise.all(
        assessmentAssigments.map(async (assessmentAssigment) => {
          await apiClient.graphql({
            query: mutations.deleteAssessmentAssignment,
            variables: { input: { id: assessmentAssigment.id } },
          });
        })
      );
      await apiClient.graphql({
        query: mutations.deleteCustomer,
        variables: { input: { id: id } },
      });
      await fetchCustomers();
      setNotificationMessage(t("clientDeleted"));
    } catch {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  async function fetchCustomers({ showLoading = true } = {}) {
    if (showLoading) setOpenLoading(true);
    try {
      const [userArr, assessmentArr, fetchedCustomers] = await Promise.all([
        listUsers(),
        listAllAssessments(),
        fetchAllCustomers(false),
      ]);
      const userById = new Map(userArr.map((user) => [user.id, user]));
      const assessmentById = new Map(
        assessmentArr.map((assessment) => [assessment.id, assessment])
      );
      const listOfCustomers = await Promise.all(
        fetchedCustomers.map(async (customer) => {
          const owners = customer.usernames.map(
            (userId) => userById.get(userId) || { id: userId, email: null }
          );
          const assessmentAssignments =
            await fetchAllAssessmentAssignmentsByCustomer(customer.id);
          const assessments = assessmentAssignments
            .map((assessmentAssignment) => {
              const assessmentData = assessmentById.get(
                assessmentAssignment.assessmentID
              );
              if (!assessmentData) return null;
              return {
                id: assessmentData.id,
                name: assessmentData.name,
                language: assessmentData.language,
                package: packages[assessmentAssignment.package],
                version: assessmentAssignment.version,
                ownerGroups: assessmentData.ownerGroups || [],
              };
            })
            .filter(Boolean);
          return {
            id: customer.id,
            name: customer.name,
            userIds: owners,
            assessments: assessments,
            trial: customer.trial,
            active: customer.active,
          };
        })
      );
      setCustomers(listOfCustomers);
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    if (showLoading) setOpenLoading(false);
  }

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer.id !== "") {
      setOpenEditCustomerModal(true);
    }
  }, [selectedCustomer]);

  return (
    <>
      <Box sx={{ mt: 5 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              borderBottom: 1,
              borderColor: CGI_RED,
            }}
          >
            <Typography
              sx={{
                color: CGI_RED,
                fontWeight: 500,
                fontSize: 23,
              }}
            >
              {t("manageCustomers")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedCustomer(emptyCustomer);
                setOpenEditCustomerModal(true);
              }}
            >
              {t("addCustomer")}
            </Button>
          </Box>
          <Box>
            <DataTable
              headers={headers}
              data={rows}
              hiddenColumns={hiddenHeaders}
              autoRowHeight={true}
            />
          </Box>
        </Container>
        <LoadingModal open={openLoading} />
        <EditCustomerModal
          open={openEditCustomerModal}
          setOpen={setOpenEditCustomerModal}
          selectedCustomer={selectedCustomer}
          allAssessments={allAssessments}
          allUsers={allUsers}
          fetchCustomers={fetchCustomers}
          setOpenLoading={setOpenLoading}
          setNotificationMessage={setNotificationMessage}
          setOpenNotification={setOpenNotification}
        />
      </Box>
      <DeletionModal
        open={openDelete}
        setOpen={setOpenDelete}
        deleteFunction={deleteCustomer}
        safeDelete={true}
        itemClass={t("customer")}
        id={toDeleteId}
        name={toDeleteName}
      />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </>
  );
}
